import { Server } from 'lincd-server-utils/utils/Server';
import { useEffect, useRef, useState } from 'react';
import { packageName } from '../package.js';

export interface TorchPresenceEvent {
  type: 'torch:lit' | 'torch:passed';
  timestamp: number;
  count: number;
  receivedFromLightId?: string;
  bearer?: {
    name?: string;
    city?: string;
    country?: string;
  };
}

export interface UseTorchPresenceOptions {
  /** When false, the hook does not subscribe. Use to gate by isOpen / paused. */
  active: boolean;
  /** Optional callback for each new event (e.g. trigger a particle burst). */
  onEvent?: (event: TorchPresenceEvent) => void;
}

const POLL_INTERVAL_MS = 10_000;
/** Maximum events kept in memory; older events are dropped on each poll cycle. */
const MAX_EVENTS = 20;

interface RawUpdate {
  type: string;
  timestamp: number;
  data: {
    count?: number;
    receivedFromLightId?: string;
    bearer?: {
      name?: string;
      city?: string;
      country?: string;
    };
  };
}

/**
 * Polls the backend for recent `torch:lit` events while `active === true`.
 * Dedupes by (timestamp, type). Caps internal event array at MAX_EVENTS.
 *
 * On first activation, starts from `Date.now()` so only events that arrive
 * AFTER the hook mounts are surfaced (no historical replay).
 */
export function useTorchPresence(opts: UseTorchPresenceOptions): {
  events: TorchPresenceEvent[];
} {
  const [events, setEvents] = useState<TorchPresenceEvent[]>([]);
  const lastSeenRef = useRef<number>(0);
  const onEventRef = useRef(opts.onEvent);

  // Keep onEvent ref current without re-firing the polling effect.
  useEffect(() => {
    onEventRef.current = opts.onEvent;
  }, [opts.onEvent]);

  useEffect(() => {
    if (!opts.active) return;

    // Initialize the watermark when this activation begins.
    lastSeenRef.current = Date.now();

    let cancelled = false;

    const tick = async () => {
      try {
        const raw = (await Server.call(
          packageName,
          'getRecentTorchEvents',
          lastSeenRef.current
        )) as RawUpdate[];
        if (cancelled || !Array.isArray(raw) || raw.length === 0) return;

        const fresh: TorchPresenceEvent[] = [];
        for (const u of raw) {
          if (u.type !== 'torch:lit' && u.type !== 'torch:passed') continue;
          if (u.timestamp <= lastSeenRef.current) continue;
          fresh.push({
            type: u.type,
            timestamp: u.timestamp,
            count: typeof u.data?.count === 'number' ? u.data.count : 0,
            receivedFromLightId:
              typeof u.data?.receivedFromLightId === 'string'
                ? u.data.receivedFromLightId
                : undefined,
            bearer: u.data?.bearer,
          });
        }
        if (fresh.length === 0) return;

        // Advance the watermark to the latest event timestamp so we don't
        // resurface the same events next poll cycle.
        lastSeenRef.current = Math.max(
          lastSeenRef.current,
          ...fresh.map((e) => e.timestamp)
        );

        setEvents((prev) => {
          const next = [...prev, ...fresh];
          return next.length > MAX_EVENTS
            ? next.slice(next.length - MAX_EVENTS)
            : next;
        });

        // Fire the optional consumer callback for each new event.
        if (onEventRef.current) {
          for (const e of fresh) onEventRef.current(e);
        }
      } catch (err) {
        // Network / backend hiccup — swallow; next tick will retry.
        console.warn('useTorchPresence: poll failed (will retry)', err);
      }
    };

    // Run once immediately so the first event lands within ~one network RTT.
    void tick();
    const interval = window.setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [opts.active]);

  return { events };
}
